import React, { useState, useRef, useEffect } from 'react';
import { useBeamTrussStore } from '../../hooks/useBeamTrussManagement';

// ADD: Module-level storage for beam input persistence (distributed loads only)
const beamInputStorage: {[beamId: string]: Partial<LoadValues>} = {};

interface BeamTrussConfigPanelProps {
  beamId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

type LoadType = 'distributed' | 'distributed2' | 'concentrated' | 'twisted';
type LoadDirection = 'dx' | 'dy' | 'dz';

interface LoadValues {
  // Distributed Type 1 (uniform)
  dx: number;
  dy: number;
  dz: number;
  
  // Distributed Type 2 (trapezoidal/triangular)
  dxLeft: number;
  dyLeft: number;
  dzLeft: number;
  dxRight: number;
  dyRight: number;
  dzRight: number;
  
  // Concentrated load
  fx: number;
  fy: number;
  fz: number;
  referenceNode: 'node1' | 'node2'; // Which node to measure from (node1 = start, node2 = end)
  percentageFromNode: number; // Percentage distance from the reference node (0-100%)
  
  // Twisted load
  torque: number;
  torqueDirection: 'clockwise' | 'anticlockwise';
}

export const BeamTrussConfigPanel: React.FC<BeamTrussConfigPanelProps> = ({
  beamId,
  position,
  onClose
}) => {
  const {
    addBeamTruss,
    removeBeamTruss,
    getTrussesForBeam,
  } = useBeamTrussStore();

  // ADD: New state variable for beam input tracking (distributed loads only) - using module storage for persistence
  const [beamInputData, setBeamInputData] = useState<{[beamId: string]: Partial<LoadValues>}>(() => beamInputStorage);

  const [selectedLoadType, setSelectedLoadType] = useState<LoadType | null>(null);
  const [selectedDirection, setSelectedDirection] = useState<LoadDirection | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [loadValues, setLoadValues] = useState<LoadValues>({
    dx: 0, dy: 0, dz: 0,
    dxLeft: 0, dyLeft: 0, dzLeft: 0,
    dxRight: 0, dyRight: 0, dzRight: 0,
    fx: 0, fy: 0, fz: 0,
    referenceNode: 'node1',
    percentageFromNode: 50,
    torque: 0,
    torqueDirection: 'clockwise'
  });

  // Drag state
  const [panelPosition, setPanelPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  const existingTrusses = getTrussesForBeam(beamId);

  // ADD: Initialize component with saved values for this beam when it first opens
  useEffect(() => {
    // Only apply fixes for distributed and distributed2
    if (selectedLoadType === 'distributed' || selectedLoadType === 'distributed2') {
      const savedInputs = beamInputData[beamId];
      if (savedInputs) {
        setLoadValues(prev => ({
          ...prev,
          ...savedInputs
        }));
      }
    }
  }, [beamId]); // Trigger when beamId changes (popup opens for different beam)

  // ADD: Load saved input values when load type changes
  useEffect(() => {
    // Only apply fixes for distributed and distributed2
    if (selectedLoadType === 'distributed' || selectedLoadType === 'distributed2') {
      const savedInputs = beamInputData[beamId];
      if (savedInputs) {
        setLoadValues(prev => ({
          ...prev,
          ...savedInputs
        }));
      }
    }
  }, [selectedLoadType, beamInputData]);

  // Update panel position when prop changes
  useEffect(() => {
    setPanelPosition(position);
  }, [position]);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && panelRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Constrain to viewport bounds
        const maxX = window.innerWidth - panelRef.current.offsetWidth;
        const maxY = window.innerHeight - panelRef.current.offsetHeight;
        
        setPanelPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleInputChange = (field: keyof LoadValues, value: number | string) => {
    setLoadValues(prev => ({
      ...prev,
      [field]: value
    }));

    // ADD: Persistence logic ONLY for distributed loads
    if (selectedLoadType === 'distributed' || selectedLoadType === 'distributed2') {
      const updatedData = {
        ...beamInputData,
        [beamId]: {
          ...beamInputData[beamId],
          [field]: value
        }
      };
      setBeamInputData(updatedData);
      // Also update module storage for persistence across component remounts
      beamInputStorage[beamId] = {
        ...beamInputStorage[beamId],
        [field]: value
      };
      
      // ADD: Zero-value removal logic ONLY for distributed loads
      if ((value === "0" || value === 0) && typeof field === 'string') {
        const axis = field as string;
        // Only remove constraints for dx, dy, dz fields (not for left/right variations)
        if (['dx', 'dy', 'dz'].includes(axis) || 
            ['dxLeft', 'dyLeft', 'dzLeft', 'dxRight', 'dyRight', 'dzRight'].includes(axis)) {
          removeSpecificConstraint(beamId, axis, selectedLoadType);
        }
      }
    }
    // Keep existing logic unchanged for concentrated loads
  };

  const handleDirectionSelect = (direction: LoadDirection) => {
    setSelectedDirection(direction);
    // Input section will automatically show when direction is selected
  };

  // ADD: Constraint removal function (distributed loads only)
  const removeSpecificConstraint = (beamId: string, axis: string, loadType: LoadType) => {
    // Only apply to distributed loads
    if (loadType === 'distributed' || loadType === 'distributed2') {
      // Get existing trusses for this beam
      const existingTrusses = getTrussesForBeam(beamId);
      
      // Find and remove trusses that match the zero-valued constraint
      existingTrusses.forEach(truss => {
        let shouldRemove = false;
        
        if (loadType === 'distributed') {
          // For distributed, check if the specific axis is zero
          if (axis === 'dx' && truss.dx === 0) shouldRemove = true;
          if (axis === 'dy' && truss.dy === 0) shouldRemove = true;
          if (axis === 'dz' && truss.dz === 0) shouldRemove = true;
        } else if (loadType === 'distributed2') {
          // For distributed2, check if both left and right values are zero
          if (axis.includes('dx') && truss.dxLeft === 0 && truss.dxRight === 0) shouldRemove = true;
          if (axis.includes('dy') && truss.dyLeft === 0 && truss.dyRight === 0) shouldRemove = true;
          if (axis.includes('dz') && truss.dzLeft === 0 && truss.dzRight === 0) shouldRemove = true;
        }
        
        if (shouldRemove) {
          removeBeamTruss(truss.id);
        }
      });
    }
    // Do nothing for concentrated loads - keep existing behavior
  };

  const handleApplyLoad = () => {
    if (!selectedLoadType) return;

    // For distributed and distributed2, require direction selection
    if ((selectedLoadType === 'distributed' || selectedLoadType === 'distributed2') && !selectedDirection) {
      return;
    }

    let loadTypeName = '';
    let directionName = '';
    // Get fresh truss data for this beam
    const currentTrusses = getTrussesForBeam(beamId);

    switch (selectedLoadType) {
      case 'distributed':
        loadTypeName = 'Distributed Uniform Load';
        // Only apply the load in the selected direction
        const dx = selectedDirection === 'dx' ? loadValues.dx : 0;
        const dy = selectedDirection === 'dy' ? loadValues.dy : 0;
        const dz = selectedDirection === 'dz' ? loadValues.dz : 0;
        
        // Check if there's already a distributed_uniform truss for this beam
        const existingUniformTruss = currentTrusses.find(truss => truss.type === 'distributed_uniform');
        
        if (existingUniformTruss) {
          // Update existing truss with new values
          const { updateBeamTruss } = useBeamTrussStore.getState();
          updateBeamTruss(existingUniformTruss.id, {
            dx: dx,
            dy: dy,
            dz: dz
          });
        } else {
          // Create new truss
          addBeamTruss(beamId, 'distributed_uniform', 0, dx, dy, dz);
        }
        break;
        
      case 'distributed2':
        loadTypeName = 'Distributed Variable Load';
        // Only apply the load in the selected direction
        const dxLeft = selectedDirection === 'dx' ? loadValues.dxLeft : 0;
        const dyLeft = selectedDirection === 'dy' ? loadValues.dyLeft : 0;
        const dzLeft = selectedDirection === 'dz' ? loadValues.dzLeft : 0;
        const dxRight = selectedDirection === 'dx' ? loadValues.dxRight : 0;
        const dyRight = selectedDirection === 'dy' ? loadValues.dyRight : 0;
        const dzRight = selectedDirection === 'dz' ? loadValues.dzRight : 0;
        
        // Check if there's already a distributed_variable truss for this beam
        const existingVariableTruss = currentTrusses.find(truss => truss.type === 'distributed_variable');
        
        if (existingVariableTruss) {
          // Update existing truss with new values
          const { updateBeamTruss } = useBeamTrussStore.getState();
          updateBeamTruss(existingVariableTruss.id, {
            dxLeft: dxLeft,
            dyLeft: dyLeft,
            dzLeft: dzLeft,
            dxRight: dxRight,
            dyRight: dyRight,
            dzRight: dzRight
          });
        } else {
          // Create new truss
          addBeamTruss(beamId, 'distributed_variable', 0, 
            dxLeft, dyLeft, dzLeft, dxRight, dyRight, dzRight
          );
        }
        break;
        
      case 'concentrated':
        loadTypeName = 'Concentrated Load';
        // Only apply the load in the selected direction
        const fx = selectedDirection === 'dx' ? loadValues.fx : 0;
        const fy = selectedDirection === 'dy' ? loadValues.fy : 0;
        const fz = selectedDirection === 'dz' ? loadValues.fz : 0;
        addBeamTruss(beamId, 'concentrated_load', 0,
          fx, fy, fz, 0, 0, 0, fx, fy, fz, 
          loadValues.referenceNode, loadValues.percentageFromNode
        );
        break;

      case 'twisted':
        loadTypeName = 'Twisted Load';
        // Check if there's already a twisted load for this beam
        const existingTwistedLoad = currentTrusses.find(truss => truss.type === 'twisted_load');
        
        if (existingTwistedLoad) {
          // Update existing twisted load
          const { updateBeamTruss } = useBeamTrussStore.getState();
          updateBeamTruss(existingTwistedLoad.id, {
            torque: loadValues.torque,
            torqueDirection: loadValues.torqueDirection
          });
        } else {
          // Create new twisted load
          addBeamTruss(beamId, 'twisted_load', 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 
            undefined, undefined, undefined, undefined, undefined, undefined,
            loadValues.torque, loadValues.torqueDirection
          );
        }
        break;
    }

    // Get direction name for display
    switch (selectedDirection) {
      case 'dx': directionName = 'X Direction'; break;
      case 'dy': directionName = 'Y Direction'; break;
      case 'dz': directionName = 'Z Direction'; break;
    }
    
    // Show success message
    if (selectedLoadType === 'twisted') {
      setSuccessMessage(`✅ ${loadTypeName} applied (${loadValues.torqueDirection})`);
    } else {
      setSuccessMessage(`✅ ${loadTypeName} applied in ${directionName}`);
    }
    
    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
    
    // console.log('✅ Load applied successfully. Panel remains open for additional loads.');
  };

  const handleCancel = () => {
    setSelectedLoadType(null);
    setSelectedDirection(null);
    setLoadValues({
      dx: 0, dy: 0, dz: 0,
      dxLeft: 0, dyLeft: 0, dzLeft: 0,
      dxRight: 0, dyRight: 0, dzRight: 0,
      fx: 0, fy: 0, fz: 0,
      referenceNode: 'node1',
      percentageFromNode: 50,
      torque: 0,
      torqueDirection: 'clockwise'
    });
  };

  const renderLoadTypeSelector = () => (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Select Load Type:
      </h4>
      <div className="space-y-2">
        <button
          onClick={() => setSelectedLoadType('distributed')}
          className="w-full px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
        >
          Distributed
        </button>
        <button
          onClick={() => setSelectedLoadType('distributed2')}
          className="w-full px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
        >
          Distributed 2
        </button>
        <button
          onClick={() => setSelectedLoadType('concentrated')}
          className="w-full px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors"
        >
          Concentrated
        </button>
        <button
          onClick={() => setSelectedLoadType('twisted')}
          className="w-full px-4 py-2 text-sm bg-purple-500 hover:bg-purple-600 text-white rounded-md transition-colors"
        >
          Twisted Load
        </button>
      </div>
    </div>
  );

  const renderDistributedInputs = () => {
    if (!selectedDirection) {
      return (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Load Direction:
          </h4>
          <div className="space-y-3">
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dx')}
            >
              <input
                type="radio"
                name="direction"
                value="dx"
                checked={selectedDirection === 'dx'}
                onChange={() => handleDirectionSelect('dx')}
                className="w-5 h-5 text-red-600 bg-gray-100 border-2 border-gray-300 focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#dc2626',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-red-600 font-medium cursor-pointer">
                DX - X Direction (Red)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dy')}
            >
              <input
                type="radio"
                name="direction"
                value="dy"
                checked={selectedDirection === 'dy'}
                onChange={() => handleDirectionSelect('dy')}
                className="w-5 h-5 text-green-600 bg-gray-100 border-2 border-gray-300 focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#16a34a',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-green-600 font-medium cursor-pointer">
                DY - Y Direction (Green)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dz')}
            >
              <input
                type="radio"
                name="direction"
                value="dz"
                checked={selectedDirection === 'dz'}
                onChange={() => handleDirectionSelect('dz')}
                className="w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#2563eb',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-blue-600 font-medium cursor-pointer">
                DZ - Z Direction (Blue)
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Show input field after direction is selected
    const getFieldName = () => selectedDirection;
    const getLabel = () => {
      return selectedDirection === 'dx' ? 'DX Value' : selectedDirection === 'dy' ? 'DY Value' : 'DZ Value';
    };
    const getColor = () => {
      return selectedDirection === 'dx' ? 'text-red-600' : selectedDirection === 'dy' ? 'text-green-600' : 'text-blue-600';
    };

    const fieldName = getFieldName() as keyof LoadValues;

    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Distributed Load (Uniform):
        </h4>
        <div>
          <label className={`block text-sm font-medium mb-2 ${getColor()}`}>
            {getLabel()}:
          </label>
          <input
            type="number"
            value={loadValues[fieldName]}
            onChange={(e) => handleInputChange(fieldName, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
            step="0.1"
            placeholder="Enter load value"
          />
        </div>
        <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-700 p-2 rounded">
          <p><strong>Selected Direction:</strong> {selectedDirection?.toUpperCase()} - {getLabel()}</p>
          <p><strong>Arrow Color:</strong> {selectedDirection === 'dx' ? 'Red' : selectedDirection === 'dy' ? 'Green' : 'Blue'} arrows along beam</p>
        </div>
      </div>
    );
  };

  const renderDistributed2Inputs = () => {
    if (!selectedDirection) {
      return (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Load Direction:
          </h4>
          <div className="space-y-3">
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dx')}
            >
              <input
                type="radio"
                name="direction"
                value="dx"
                checked={selectedDirection === 'dx'}
                onChange={() => handleDirectionSelect('dx')}
                className="w-5 h-5 text-red-600 bg-gray-100 border-2 border-gray-300 focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#dc2626',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-red-600 font-medium cursor-pointer">
                DX - X Direction (Red)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dy')}
            >
              <input
                type="radio"
                name="direction"
                value="dy"
                checked={selectedDirection === 'dy'}
                onChange={() => handleDirectionSelect('dy')}
                className="w-5 h-5 text-green-600 bg-gray-100 border-2 border-gray-300 focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#16a34a',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-green-600 font-medium cursor-pointer">
                DY - Y Direction (Green)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dz')}
            >
              <input
                type="radio"
                name="direction"
                value="dz"
                checked={selectedDirection === 'dz'}
                onChange={() => handleDirectionSelect('dz')}
                className="w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#2563eb',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-blue-600 font-medium cursor-pointer">
                DZ - Z Direction (Blue)
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Show input fields after direction is selected
    const getLabel = () => {
      return selectedDirection === 'dx' ? 'DX Value' : selectedDirection === 'dy' ? 'DY Value' : 'DZ Value';
    };
    const getColor = () => {
      return selectedDirection === 'dx' ? 'text-red-600' : selectedDirection === 'dy' ? 'text-green-600' : 'text-blue-600';
    };

    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Distributed Load (Variable):
        </h4>
        
        {/* Left Side Values */}
        <div>
          <h5 className="text-xs font-semibold mb-2 text-gray-600 dark:text-gray-400">
            Left Side Value:
          </h5>
          <div>
            <label className={`block text-xs font-medium mb-1 ${getColor()}`}>
              {getLabel()} Left
            </label>
            <input
              type="number"
              value={loadValues[`${selectedDirection}Left` as keyof LoadValues]}
              onChange={(e) => handleInputChange(`${selectedDirection}Left` as keyof LoadValues, parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              step="0.1"
              placeholder="Enter left side value"
            />
          </div>
        </div>

        {/* Right Side Values */}
        <div>
          <h5 className="text-xs font-semibold mb-2 text-gray-600 dark:text-gray-400">
            Right Side Value:
          </h5>
          <div>
            <label className={`block text-xs font-medium mb-1 ${getColor()}`}>
              {getLabel()} Right
            </label>
            <input
              type="number"
              value={loadValues[`${selectedDirection}Right` as keyof LoadValues]}
              onChange={(e) => handleInputChange(`${selectedDirection}Right` as keyof LoadValues, parseFloat(e.target.value) || 0)}
              className="w-full px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              step="0.1"
              placeholder="Enter right side value"
            />
          </div>
        </div>

        <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-700 p-2 rounded">
          <p><strong>Variable Distribution:</strong> Creates trapezoidal/triangular load patterns</p>
          <p><strong>Arrow sizes vary:</strong> Left to right based on input values</p>
          <p><strong>Selected Direction:</strong> {selectedDirection?.toUpperCase()} - {getColor().includes('red') ? 'Red' : getColor().includes('green') ? 'Green' : 'Blue'} arrows</p>
        </div>
      </div>
    );
  };

  const renderConcentratedInputs = () => {
    if (!selectedDirection) {
      return (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Select Force Direction:
          </h4>
          <div className="space-y-3">
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dx')}
            >
              <input
                type="radio"
                name="direction"
                value="dx"
                checked={selectedDirection === 'dx'}
                onChange={() => handleDirectionSelect('dx')}
                className="w-5 h-5 text-red-600 bg-gray-100 border-2 border-gray-300 focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#dc2626',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-red-600 font-medium cursor-pointer">
                FX - X Direction (Red)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dy')}
            >
              <input
                type="radio"
                name="direction"
                value="dy"
                checked={selectedDirection === 'dy'}
                onChange={() => handleDirectionSelect('dy')}
                className="w-5 h-5 text-green-600 bg-gray-100 border-2 border-gray-300 focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#16a34a',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-green-600 font-medium cursor-pointer">
                FY - Y Direction (Green)
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleDirectionSelect('dz')}
            >
              <input
                type="radio"
                name="direction"
                value="dz"
                checked={selectedDirection === 'dz'}
                onChange={() => handleDirectionSelect('dz')}
                className="w-5 h-5 text-blue-600 bg-gray-100 border-2 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#2563eb',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-blue-600 font-medium cursor-pointer">
                FZ - Z Direction (Blue)
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Show input fields after direction is selected
    const getFieldName = () => {
      return selectedDirection === 'dx' ? 'fx' : selectedDirection === 'dy' ? 'fy' : 'fz';
    };
    const getLabel = () => {
      return selectedDirection === 'dx' ? 'FX (N)' : selectedDirection === 'dy' ? 'FY (N)' : 'FZ (N)';
    };
    const getColor = () => {
      return selectedDirection === 'dx' ? 'text-red-600' : selectedDirection === 'dy' ? 'text-green-600' : 'text-blue-600';
    };

    const fieldName = getFieldName() as keyof LoadValues;

    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Concentrated Load:
        </h4>
        
        {/* Force Component */}
        <div>
          <label className={`block text-sm font-medium mb-2 ${getColor()}`}>
            {getLabel()}:
          </label>
          <input
            type="number"
            value={loadValues[fieldName]}
            onChange={(e) => handleInputChange(fieldName, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
            step="1"
            placeholder="Enter force value in Newtons"
          />
        </div>

        {/* Node-based positioning */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Reference Node:
            </label>
            <select
              value={loadValues.referenceNode}
              onChange={(e) => handleInputChange('referenceNode', e.target.value as 'node1' | 'node2')}
              className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="node1">Node 1 (Start Node)</option>
              <option value="node2">Node 2 (End Node)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Distance from Selected Node (%):
            </label>
            <input
              type="number"
              value={loadValues.percentageFromNode}
              onChange={(e) => handleInputChange('percentageFromNode', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              step="1"
              min="0"
              max="100"
              placeholder="0-100% from selected node"
            />
          </div>
        </div>

        <div className="text-xs text-gray-500 bg-gray-50 dark:bg-gray-700 p-2 rounded">
          <p><strong>Single arrow:</strong> Size proportional to force magnitude</p>
          <p><strong>Position:</strong> Percentage distance from selected node (0% = at node, 100% = at opposite node)</p>
          <p><strong>Selected Direction:</strong> {getLabel()} - {getColor().includes('red') ? 'Red' : getColor().includes('green') ? 'Green' : 'Blue'} arrow</p>
        </div>
      </div>
    );
  };

  const renderTwistedInputs = () => {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Twisted Load (Torsional Moment):
        </h4>
        
        {/* Torque Input */}
        <div>
          <label className="block text-sm font-medium mb-2 text-purple-600 dark:text-purple-400">
            Torque (N·m):
          </label>
          <input
            type="number"
            value={loadValues.torque}
            onChange={(e) => handleInputChange('torque', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
            step="0.1"
            placeholder="Enter torque value in Newton-meters"
          />
        </div>

        {/* Direction Selection */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Rotation Direction:
          </label>
          <div className="space-y-3">
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleInputChange('torqueDirection', 'clockwise')}
            >
              <input
                type="radio"
                name="torqueDirection"
                value="clockwise"
                checked={loadValues.torqueDirection === 'clockwise'}
                onChange={() => handleInputChange('torqueDirection', 'clockwise')}
                className="w-5 h-5 text-purple-600 bg-gray-100 border-2 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#9333ea',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-purple-600 dark:text-purple-400 font-medium cursor-pointer">
                Clockwise
              </span>
            </div>
            <div 
              className="flex items-center space-x-3 cursor-pointer p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleInputChange('torqueDirection', 'anticlockwise')}
            >
              <input
                type="radio"
                name="torqueDirection"
                value="anticlockwise"
                checked={loadValues.torqueDirection === 'anticlockwise'}
                onChange={() => handleInputChange('torqueDirection', 'anticlockwise')}
                className="w-5 h-5 text-purple-600 bg-gray-100 border-2 border-gray-300 focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                style={{ 
                  accentColor: '#9333ea',
                  transform: 'scale(1.2)'
                }}
              />
              <span className="text-sm text-purple-600 dark:text-purple-400 font-medium cursor-pointer">
                Anti-clockwise
              </span>
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-500 bg-purple-50 dark:bg-gray-700 p-2 rounded border border-purple-200 dark:border-purple-900">
          <p><strong>Visualization:</strong> 75% circular purple arrow at beam midpoint</p>
          <p><strong>Direction:</strong> Arrow wraps around beam in selected rotation direction</p>
          <p><strong>Size:</strong> Arrow size proportional to torque magnitude</p>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={panelRef}
      className={`fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-96 z-50 border-2 border-gray-200 dark:border-gray-600 ${
        isDragging ? 'cursor-grabbing shadow-2xl' : 'cursor-auto'
      }`}
      style={{
        left: panelPosition.x + 'px',
        top: panelPosition.y + 'px',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}
    >
      {/* Draggable Header */}
      <div 
        className={`flex justify-between items-center p-4 pb-2 border-b border-gray-200 dark:border-gray-600 ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={handleMouseDown}
      >
        <h3 className="text-lg font-semibold dark:text-white select-none">Distributed Load</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl font-bold cursor-pointer"
        >
          ✕
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mx-4 mt-2 p-3 bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-md">
          <p className="text-sm text-green-800 dark:text-green-200 text-center font-medium">
            {successMessage}
          </p>
        </div>
      )}

      {/* Content Area */}
      <div className="p-4">
        <div className="space-y-4">
        {!selectedLoadType && renderLoadTypeSelector()}

        {selectedLoadType === 'distributed' && (
          <div className="space-y-4">
            {renderDistributedInputs()}
            {selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleApplyLoad}
                  className="flex-1 px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                >
                  Apply Load
                </button>
                <button
                  onClick={() => setSelectedDirection(null)}
                  className="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Direction
                </button>
              </div>
            )}
            {!selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Load Type
                </button>
              </div>
            )}
          </div>
        )}

        {selectedLoadType === 'distributed2' && (
          <div className="space-y-4">
            {renderDistributed2Inputs()}
            {selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleApplyLoad}
                  className="flex-1 px-4 py-2 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md transition-colors"
                >
                  Apply Load
                </button>
                <button
                  onClick={() => setSelectedDirection(null)}
                  className="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Direction
                </button>
              </div>
            )}
            {!selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Load Type
                </button>
              </div>
            )}
          </div>
        )}

        {selectedLoadType === 'concentrated' && (
          <div className="space-y-4">
            {renderConcentratedInputs()}
            {selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleApplyLoad}
                  className="flex-1 px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md transition-colors"
                >
                  Apply Load
                </button>
                <button
                  onClick={() => setSelectedDirection(null)}
                  className="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Direction
                </button>
              </div>
            )}
            {!selectedDirection && (
              <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
                >
                  Back to Load Type
                </button>
              </div>
            )}
          </div>
        )}

        {selectedLoadType === 'twisted' && (
          <div className="space-y-4">
            {renderTwistedInputs()}
            <div className="flex gap-2 pt-2 border-t dark:border-gray-600">
              <button
                onClick={handleApplyLoad}
                className="flex-1 px-4 py-2 text-sm bg-purple-500 hover:bg-purple-600 text-white rounded-md transition-colors"
              >
                Apply Load
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm bg-gray-500 hover:bg-gray-600 text-white rounded-md transition-colors"
              >
                Back to Load Type
              </button>
            </div>
          </div>
        )}

        {/* Existing Loads Display - ONLY for concentrated loads */}
        {selectedLoadType === 'concentrated' && existingTrusses.filter(truss => truss.type === 'concentrated_load').length > 0 && (
          <div className="space-y-3 pt-4 border-t dark:border-gray-600">
            <label className="block text-sm font-medium mb-2 dark:text-gray-300">
              Existing Concentrated Loads:
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {existingTrusses
                .filter(truss => truss.type === 'concentrated_load') // Only show concentrated loads
                .map((truss) => (
                <div
                  key={truss.id}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: truss.color }}
                    />
                    <span className="text-xs dark:text-gray-300">
                      {truss.label} ({truss.type})
                    </span>
                  </div>
                  <button
                    onClick={() => removeBeamTruss(truss.id)}
                    className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded"
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end pt-2 border-t dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};
